import { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { Input } from '../../Components/UI/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../Components/UI/table';
import { Card, CardContent } from '../../Components/UI/card';
import { Search, Check, ArrowUp, ArrowDown, Filter, Globe, ListTodo, UserCheck, MessageSquare } from 'lucide-react';
import ColumnPreferencesDropdown from '../../Components/Common/ColumnPrefereceDD';
import { useDispatch, useSelector } from 'react-redux';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '../../Components/UI/tooltip';
import { BsWhatsapp } from 'react-icons/bs';
import { MdPhone } from 'react-icons/md';
import { dateDiff, getColorClass, getTaskColor } from '../../Utils/Utilities';
import Pagination from '../../Components/Common/Pagination';
import useIsMobile from '../../Hooks/useIsMobile';
import ContactActions from '../../Components/Common/ContactActions';
import { toast } from 'sonner';
import { setGlobalLoading } from "../../Store/Actions";
import ConnectionDetailsWrapper from '../Connections/ConnectionDetailsWrapper';
import { getAgencyStatsAPI } from './ReportAPI';
import WebstoreSetupProgressModal from './WebstoreSetupProgressModal';
import UsageMISWspModal from './UsageMISWspModal';
import { AlertBox } from '../../Components/Common/AlertBox';
import { getTableDateOrWeekday, formatDateWithTime } from '../../Utils/DateTime';
import { getThreadTypesArr, getTransactionTypesArr } from '../../Store/Reducers';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@radix-ui/react-hover-card';
import ThreadSahiIcon from "../../Components/Common/ThreadSahiIcon";
import { DatePickerWithRange } from '../../Components/Common/DatePickerWithRange';
import { CommonFilter } from '../../Components/Common/CommonFilter';
import { cn } from '../../Lib/Cn';
import { Button } from '../../Components/UI/button';
import { FormatAmount, getSubscriptionPlans } from '../../Utils/Utilities';
import { startOfDay, isWithinInterval, isAfter, isBefore, isSameDay, differenceInDays } from 'date-fns';

const usageMISClasses = {
	spacing: 'space-y-3 lg:space-y-4',
	flexRow: 'flex flex-row',
	flexCol: 'flex flex-col f-col',
	gap2: 'gap-2',
	gap3: 'gap-3',
	gap5: 'gap-5',
	card: 'bg-sahi-white',
	cardHidden: 'bg-sahi-white hidden lg:block !pt-0 !max-h-[calc(100vh-300px)]',
	cardRadius: 'sahi-radius',
	shadow: 'shadow-sm',
	border: 'border border-gray-200',
	tableCell: 'text-left pl-4',
	tableCellRight: 'text-right pr-4',
	tableCellMin: 'min-w-[100px] max-w-[120px]',
	tableCellEllipsis: 'overflow-hidden text-ellipsis whitespace-nowrap',
	tableRow: 'hover:bg-gray-50 cursor-pointer',
	mobileCard: 'bg-sahi-white sahi-radius shadow-sm border border-gray-200 mb-2 w-full f-col justify-between',
	mobileHeader: 'f-row justify-between mobile-tile-header-layout mb-1.5',
	mobileFooter: 'flex h-[38px] justify-between items-center text-xs border-t border-[#f1f1f1] w-full mobile-tile-footer-layout px-2',
	textMain: 'text-text-color',
	textSub: 'text-sub-text-color',
	textBold: 'font-semibold',
	textMedium: 'font-medium',
	textSm: 'text-sm',
	textXs: 'text-xs',
	iconSm: 'w-4 h-4',
	iconMd: 'w-4.5 h-4.5',
	hoverCard: 'bg-white cursor-default rounded-md shadow-2xl p-2 text-sm w-fit text-left -mt-2 z-[9999]',
	hoverCardHeader: 'font-semibold text-base text-black px-2 py-2 border-b border-gray-200 overflow-hidden text-ellipsis whitespace-nowrap',
	hoverCardItem: 'flex items-center gap-2 px-2 py-1 hover:bg-gray-100',
	searchContainer: 'relative w-120 sahi-radius filter-border',
	searchInput: 'pl-10 h-10 border-none focus:outline-none focus:ring-0 shadow-none',
	searchIcon: 'absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dropdown-white',
};

const tableHeaderColumns = [
	{ key: 'agencyName', label: 'Business', className: 'align-middle min-w-[300px]' },
	{ key: 'contactName', label: 'Contact', className: 'align-middle min-w-[220px]' },
	{ key: 'subType', label: 'Subscription', className: 'align-middle' },
	{ key: 'edition', label: 'Edition', className: 'align-middle' },
	{ key: 'sinceDT', label: 'Joined on', className: 'align-middle' },
	{ key: 'lastSubDT', label: 'Paid on', className: 'align-middle' },
	{ key: 'subsTillDT', label: 'Expires on', className: 'align-middle' },
	{ key: 'lastActiveDT', label: 'Last Activity', className: 'align-middle' },
	{ key: 'lastSyncDT', label: 'Last Online', className: 'align-middle' },
	{ key: 'userCnt', label: 'Users', className: 'align-middle text-right' },
	{ key: 'connCnt', label: 'Contacts', className: 'align-middle text-right' },
	{ key: 'thrCnt', label: 'Threads', className: 'align-middle text-right' },
	{ key: 'tskCnt', label: 'Tasks', className: 'align-middle text-right' },
	{ key: 'ctrcCnt', label: 'Contracts', className: 'align-middle text-right' },
	{ key: 'txnCnt', label: 'Transactions', className: 'align-middle text-right' },
	// { key: 'lastConnDT', label: 'Last Contact', className: 'align-middle' },
	// { key: 'lastThrDT', label: 'Last Thread', className: 'align-middle' },
	// { key: 'lastTskDT', label: 'Last Task', className: 'align-middle' },
	// { key: 'lastCtrcDT', label: 'Last Contract', className: 'align-middle' },
	// { key: 'lastTxnDT', label: 'Last Transaction', className: 'align-middle' },
	{ key: 'upgdAttemptCnt', label: 'Upgrade Attempt', className: 'align-middle text-right' },
	// { key: 'lastUpgdAttemptDT', label: 'Last Upgrade Attempt', className: 'align-middle' },
	// { key: 'lastThrSentDT', label: 'Last Thread Sent', className: 'align-middle' },
	// { key: 'lastSentiReqDT', label: 'Last Sentiment Request', className: 'align-middle' },
	{ key: 'inactivityDays', label: 'Inactivity', className: 'align-middle text-right' },
];

const numericKeys = ['connCnt', 'ctrcCnt', 'userCnt', 'thrCnt', 'txnCnt', 'tskCnt', 'upgdAttemptCnt'];

const dateKeys = ['sinceDT', 'lastActiveDT', 'lastSyncDT', 'lastConnDT', 'lastThrDT', 'lastCtrcDT', 'lastTxnDT', 'lastTskDT',
	'subsTillDT', 'lastUpgdAttemptDT', 'lastThrSentDT', 'lastSentiReqDT', 'lastSubDT'];


const UsageMIS = () => {
	const isMobile = useIsMobile();
	const dispatch = useDispatch();
	const headerRefs = useRef([]);
	const tableContainerRef = useRef(null);
	const scrollPositionRef = useRef({ scrollX: 0, scrollY: 0 });
	const isScrollingRef = useRef(false);
	const [agencies, setAgencies] = useState([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [currentPage, setCurrentPage] = useState(1);
	const [isDetailOpen, setIsDetailOpen] = useState(false);
	const [selectedAgency, setSelectedAgency] = useState(null);
	const [isWebstoreModalOpen, setIsWebstoreModalOpen] = useState(false);
	const [selectedWebstoreAgency, setSelectedWebstoreAgency] = useState(null);
	const [isWspModalOpen, setIsWspModalOpen] = useState(false);
	const [selectedWspAgency, setSelectedWspAgency] = useState(null);
	const [isInvalidMoveAlertOpen, setIsInvalidMoveAlertOpen] = useState(false);
	const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
	const [isFilterSelected, setIsFilterSelected] = useState(true);
	const [subType, setSubsType] = useState();
	const [ednType, setEdnType] = useState();
	const [activeDays, setActiveDays] = useState('');
	const [showEdition, setShowEdition] = useState(true);
	const [noActionsChecked, setNoActionsChecked] = useState(false);
	const [anyActionsChecked, setAnyActionsChecked] = useState(false);

	const defaultColumnVisibility = {
		agencyName: true,
		contactName: true,
		subType: true,
		edition: true,
		sinceDT: true,
		lastSubDT: true,
		subsTillDT: true,
		lastActiveDT: true,
		lastSyncDT: true,
		userCnt: true,
		connCnt: true,
		thrCnt: true,
		tskCnt: true,
		ctrcCnt: true,
		txnCnt: true,
		upgdAttemptCnt: true,
		inactivityDays: true
	};

	const getInitialColumnVisibility = () => {
		try {
			const stored = localStorage.getItem('UsageMISColPrefs');
			if (stored) {
				return { ...defaultColumnVisibility, ...JSON.parse(stored) };
			}
		} catch (error) {
			console.error('Error reading column preferences from localStorage:', error);
		}
		return defaultColumnVisibility;
	};

	const [columnVisibility, setColumnVisibility] = useState(getInitialColumnVisibility());

	const thrTypes = useSelector(getThreadTypesArr);
	const txnTypes = useSelector(getTransactionTypesArr);
	const allConnections = useSelector(state => state.connectionData?.data || []);
	const allThreads = useSelector(state => state.threadData?.data || []);
	const ednPlans = getSubscriptionPlans();

	let activeFilterClass = isFilterSelected && !isMobile ? "filter-active" : "";
	let filterIconToggleClass = "h-4 w-4 " + (isFilterSelected ? "text-sahi-primary-color" : "text-white group-hover:text-sahi-primary-color");
	var filterBtn = "button-config sahi-primary-button flex items-center justify-center group px-2.5 py-[5px]";

	const [filters, setFilters] = useState({
		subType: 'all',
		ednType: 'all',
		activeDays: null,
		joinedRange: { fromDT: null, toDT: null },
		expiresRange: { fromDT: null, toDT: null },
		upgdOnRange: { fromDT: null, toDT: null },
		noActions: false,
		anyActions: false,
		repeatUsers: false,
		activeToday: false,
		onlineToday: false,
	});

	const subOptions = useMemo(() => {
		return [
			{ subID: "All Subscriptions", value: "All Subscriptions" },
			{ subID: "Free", value: "Free" },
			{ subID: "Trial", value: "Trial" },
			{ subID: "Premium", value: "Premium" }
		];
	}, []);

	const ednOptions = useMemo(() => {
		return [
			{ subID: "All Editions", value: "All Editions" },
			...ednPlans.map(plan => ({
				subID: plan.sName,
				value: plan.sName
			}))
		];
	}, []);

	const hasActiveFilters = useMemo(() => {
		return (
			filters.subType !== 'all' ||
			filters.ednType !== 'all' ||
			filters.joinedRange?.fromDT !== null ||
			filters.joinedRange?.toDT !== null ||
			filters.expiresRange?.fromDT !== null ||
			filters.expiresRange?.toDT !== null ||
			filters.upgdOnRange?.fromDT !== null ||
			filters.upgdOnRange?.toDT !== null ||
			filters.activeDays !== null ||
			filters.noActions !== false ||
			filters.anyActions !== false ||
			filters.repeatUsers !== false ||
			filters.activeToday !== false ||
			filters.onlineToday !== false
		);
	}, [filters]);

	const handleSubsChange = useCallback((value) => {
		if (!value || value === 'all' || value === 'All Subscriptions') {
			setSubsType(undefined);
			setFilters(p => ({ ...p, subType: 'all' }));
			setShowEdition(true);
			return;
		}
		setSubsType(value);
		setFilters(p => ({
			...p,
			subType: value,
			...(value === 'Free' && { ednType: 'all' }),
			...(value === 'Trial' && { ednType: 'all' })
		}));
		//setShowEdition(value !== 'Free' && value !== 'Trial');
		if (value === 'Free' || value === 'Trial') {
			setEdnType(undefined);
		}
	}, []);

	const handleEdnChange = useCallback((value) => {
		if (!value || value === 'all' || value === 'All Editions') {
			setEdnType(undefined);
			setFilters(p => ({ ...p, ednType: 'all' }));
			return;
		}
		setEdnType(value);
		setFilters(p => ({ ...p, ednType: value }));
	}, []);

	const saveScrollPosition = useCallback(() => {
		if (tableContainerRef.current) {
			scrollPositionRef.current = {
				scrollX: tableContainerRef.current.scrollLeft,
				scrollY: tableContainerRef.current.scrollTop,
			};
		}
	}, []);

	const restoreScrollPosition = useCallback(() => {
		if (tableContainerRef.current) {
			setTimeout(() => {
				tableContainerRef.current.scrollLeft = scrollPositionRef.current.scrollX;
				tableContainerRef.current.scrollTop = scrollPositionRef.current.scrollY;
			}, 0);
		}
	}, []);


	const handleRowClick = useCallback((agency, e) => {
		e.preventDefault();
		e.stopPropagation();

		if (tableContainerRef.current) {
			scrollPositionRef.current = {
				scrollX: tableContainerRef.current.scrollLeft,
				scrollY: tableContainerRef.current.scrollTop,
			};
			isScrollingRef.current = true;
		}

		const connID = Number(agency?.connID || 0);
		const isConnection = connID > 0;

		if (isConnection) {
			setSelectedAgency(agency);
			setIsDetailOpen(true);
		} else {
			setIsInvalidMoveAlertOpen(true);
		}

		setTimeout(() => {
			if (tableContainerRef.current && isScrollingRef.current) {
				tableContainerRef.current.scrollLeft = scrollPositionRef.current.scrollX;
				tableContainerRef.current.scrollTop = scrollPositionRef.current.scrollY;
			}
		}, 0);
	}, []);

	const handleWebstoreClick = useCallback((agency, e) => {
		e?.stopPropagation?.();
		setSelectedWebstoreAgency(agency);
		setIsWebstoreModalOpen(true);
	}, []);

	const handleDetailClose = () => {
		setIsDetailOpen(false);
		setSelectedAgency(null);

		setTimeout(() => {
			if (isScrollingRef.current) {
				isScrollingRef.current = false;
			}
		}, 800);
	};

	// Handle column visibility update and save to localStorage
	const handleColumnVisibilityUpdate = (newColumnVisibility) => {
		setColumnVisibility(newColumnVisibility);
		try {
			localStorage.setItem('UsageMISColPrefs', JSON.stringify(newColumnVisibility));
		} catch (error) {
			console.error('Error saving column preferences to localStorage:', error);
		}
	};

	const formatDate = (date, defaultValue = '-') =>
		date ? getTableDateOrWeekday(date) : defaultValue;

	const getEdnName = useCallback((editionID) => {
		const ednID = Number(editionID);
		const plan = ednPlans.find(p => p.editionID === ednID);
		return plan ? plan.sName : '-';
	}, []);

	const getTodayColor = () => {
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		return getColorClass(getTaskColor(today));
	};

	const todayColor = getTodayColor();

	const calculateInactivityDays = (lastActiveDate) => {
		if (!lastActiveDate) return '-';
		const today = new Date();
		const lastActive = new Date(lastActiveDate);
		return dateDiff(lastActive, today);
	};

	const thrTypeBase = useMemo(() =>
		thrTypes.map(currThreadType => ({
			thrTypID: currThreadType.TID,
			thrTypName: currThreadType.TypSng || `Type ${currThreadType.TID}`,
			count: 0,
		})),
		[thrTypes]
	);

	const txnTypeBase = useMemo(() =>
		txnTypes.reduce((acc, currTxnType) => {
			const typ = currTxnType.Typ || 'Unknown';
			if (!acc[typ]) acc[typ] = { Typ: typ, types: [] };
			acc[typ].types.push({
				txnTypeID: currTxnType.XnTID,
				txnTypeName: currTxnType.LblF || `Type ${currTxnType.XnTID}`,
				count: 0,
			});
			return acc;
		}, {}),
		[txnTypes]
	);

	const fetchAgencyStats = useCallback(async () => {
		dispatch(setGlobalLoading(true));

		try {
			const data = await getAgencyStatsAPI({});

			if (!Array.isArray(data)) {
				throw new Error('Something went wrong.');
			}

			let parsedThrTypes = [];
			let parsedTxnTypes = [];
			let thrTypWiseCnt = [];
			let txnTypWiseCnt = [];

			const formattedData = data.map(currItem => {
				parsedThrTypes = currItem.CntTypewiseThreads ? JSON.parse(currItem.CntTypewiseThreads) : [];
				parsedTxnTypes = currItem.CntTypewiseXns ? JSON.parse(currItem.CntTypewiseXns) : [];

				thrTypWiseCnt = thrTypeBase.map(currThrType => ({
					...currThrType,
					count: parsedThrTypes.find(currThread => currThread.TID === currThrType.thrTypID)?.Rcvd || 0,
				}));

				txnTypWiseCnt = Object.values(txnTypeBase).map(currGroup => ({
					Typ: currGroup.Typ,
					types: currGroup.types.map(currType => ({
						...currType,
						count: parsedTxnTypes.find(currTxn => currTxn.TID === currType.txnTypeID)?.Cnt || 0,
					})),
				}));

				let connectionDetails = null, threadDetails = null;
				let connMobile = '';
				let connEmail = '';
				let connContactsSearch = '';

				if (currItem.SCConnID) {
					const connectionData = allConnections.find(conn => conn.ConnID === currItem.SCConnID);

					if (connectionData) {
						connectionDetails = {
							sinceDT: connectionData.SinceDT,
							source: connectionData.Source,
						};

						connMobile = connectionData.Mobile || connectionData.mobile || connectionData.MNo || '';
						connEmail = connectionData.Email || connectionData.email || connectionData.Eml || '';
						connContactsSearch = (connectionData.ContactName || '').toLowerCase();
						if (connectionData.Contacts) {
							try {
								const parsedContacts = typeof connectionData.Contacts === 'string'
									? JSON.parse(connectionData.Contacts)
									: connectionData.Contacts;
								if (Array.isArray(parsedContacts) && parsedContacts.length > 0) {
									connContactsSearch += ' ' + parsedContacts
										.map(c => `${c.CNm || ''} ${c.MNo || ''} ${c.Eml || ''}`)
										.join(' ')
										.toLowerCase();
									if (!connEmail) {
										const primaryContact = parsedContacts.find(c => c.isPrimary || c.IsPrimary) || parsedContacts[0];
										connEmail = primaryContact?.Eml || primaryContact?.Email || primaryContact?.email || '';
									}
								}
							} catch (e) {
								console.error('Error parsing contacts in UsageMIS:', e);
							}
						}

						// Fetch thread data if LastThreadID exists
						if (connectionData.LastThreadID) {
							const threadData = allThreads.find(thread => thread.ThreadID === connectionData.LastThreadID);

							if (threadData) {
								threadDetails = {
									sinceDT: threadData.SinceDT,
									source: threadData.Source,
								};
							}
						}
					}
				}

				return {
					agencyID: currItem.AgencyID,
					agencyName: currItem.AgencyName || '-',
					contactName: currItem.AdminUserName || '-',
					subType: currItem.SubTypeID === 2 ? 'Premium' : 'Free',
					ednID: currItem.EditionID || '-',
					ednName: getEdnName(currItem.EditionID),
					sinceDT: currItem.CreatedDT,
					sinceDTF: formatDate(currItem.CreatedDT),
					connCnt: currItem.CntConns || 0,
					ctrcCnt: currItem.CntContracts || 0,
					userCnt: currItem.CntUsers || 0,
					thrCnt: currItem.CntThreads || 0,
					txnCnt: currItem.CntAllXns || 0,
					upgdAttemptCnt: currItem.CntUpgdAtmpt || 0,
					tskCnt: currItem.CntTasks || 0,
					lastActiveDT: currItem.LastActiveDT,
					lastActiveDTF: formatDate(currItem.LastActiveDT),
					lastSyncDT: currItem.LastSyncDT,
					lastSyncDTF: formatDate(currItem.LastSyncDT),
					lastConnDT: currItem.LastConnDT,
					lastConnDTF: formatDate(currItem.LastConnDT),
					lastThrDT: currItem.LastThrDT,
					lastThrDTF: formatDate(currItem.LastThrDT),
					lastCtrcDT: currItem.LastContractDT,
					lastCtrcDTF: formatDate(currItem.LastContractDT),
					lastTxnDT: currItem.LastXnDT,
					lastSubDT: currItem.LastUpgdDT,
					lastTxnDTF: formatDate(currItem.LastXnDT),
					lastSubDTF: formatDate(currItem.LastUpgdDT),
					lastTskDT: currItem.LastTaskDT,
					lastTskDTF: formatDate(currItem.LastTaskDT),
					lastTaskActionDT: currItem.LastTaskActionDT,
					connTskCnt: currItem.CntConnTasks || 0,
					lastUpgdAttemptDT: currItem.LastUpgdAtmptDT,
					lastUpgdAttemptDTF: formatDate(currItem.LastUpgdAtmptDT),
					lastThrSentDT: currItem.LastThrSentDT,
					lastThrSentDTF: formatDate(currItem.LastThrSentDT),
					lastSentiReqDT: currItem.LastSentiReqDT,
					lastSentiReqDTF: formatDate(currItem.LastSentiReqDT),
					inactivityDays: calculateInactivityDays(currItem.LastActiveDT),
					thrTypWiseCnt,
					txnTypWiseCnt,
					mobile: currItem.Mobile || currItem.mobile || connMobile || '',
					email: currItem.Email || currItem.email || currItem.AdminEmail || currItem.ContactEmail || connEmail || '',
					cc: currItem.CC || '',
					subsTillDT: currItem.SubTillDT,
					subsTillDTF: formatDate(currItem.SubTillDT),
					connID: currItem.SCConnID,
					connectionDetails,
					threadDetails,
					contactsSearch: connContactsSearch,
					signUpSrc: currItem.SignUpSrc
				};
			});

			setAgencies(formattedData);
		} catch (err) {
			toast.error(err.message || "Failed to fetch agency stats");
		} finally {
			dispatch(setGlobalLoading(false));
		}
	}, [thrTypeBase, txnTypeBase, dispatch, allConnections, allThreads]);

	useEffect(() => {
		fetchAgencyStats();
		return () => {
		};
	}, [fetchAgencyStats]);

	useEffect(() => {
	}, [agencies]);

	const sortData = useCallback((key) => {
		if (!key) return;
		saveScrollPosition();
		const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
		setSortConfig({ key, direction });
		setTimeout(() => {
			restoreScrollPosition();
		}, 0);
	}, [sortConfig, saveScrollPosition, restoreScrollPosition]);

	const filteredAgencies = useMemo(() => {
		const term = searchTerm?.toLowerCase().trim();

		return agencies.filter((agency) => {
			if (
				term &&
				!agency.agencyName.toLowerCase().includes(term) &&
				!agency.contactName.toLowerCase().includes(term) &&
				!agency.mobile.toLowerCase().includes(term) &&
				!agency.email.toLowerCase().includes(term) &&
				!agency.contactsSearch?.includes(term)
			) {
				return false;
			}

			if (filters.subType !== 'all') {
				if (filters.subType === 'Free') {
					if (agency.subType !== 'Free' || agency.subsTillDT !== null) {
						return false;
					}
				} else if (filters.subType === 'Trial') {
					if (agency.subType !== 'Free' || agency.subsTillDT === null) {
						return false;
					}
				} else if (filters.subType === 'Premium') {
					if (agency.subType !== 'Premium') {
						return false;
					}
				}
			}

			if (filters.ednType !== 'all' && agency.ednName !== filters.ednType) {
				return false;
			}

			if (filters.activeDays !== null && filters.activeDays >= 0) {
				const lastActiveDT = agency.lastActiveDT ? new Date(agency.lastActiveDT) : null;
				if (!lastActiveDT) return false;

				const today = startOfDay(new Date());
				const lastActiveDay = startOfDay(lastActiveDT);
				const daysDiff = differenceInDays(today, lastActiveDay);

				const maxAllowedDaysAgo = filters.activeDays === 0 ? 0 : filters.activeDays - 1;

				if (daysDiff > maxAllowedDaysAgo) return false;
			}

			if (filters.joinedRange?.fromDT || filters.joinedRange?.toDT) {
				const joinedDT = agency.sinceDT ? new Date(agency.sinceDT) : null;
				if (!joinedDT) return false;

				const joinDT = startOfDay(joinedDT);
				const fromDT = filters.joinedRange.fromDT ? startOfDay(filters.joinedRange.fromDT) : null;
				const toDT = filters.joinedRange.toDT ? startOfDay(filters.joinedRange.toDT) : null;

				if (fromDT && toDT && !isWithinInterval(joinDT, { start: fromDT, end: toDT })) return false;
				if (fromDT && isBefore(joinDT, fromDT)) return false;
				if (toDT && isAfter(joinDT, toDT)) return false;
			}
			if (filters.expiresRange?.fromDT || filters.expiresRange?.toDT) {
				const expiresDT = agency.subsTillDT ? new Date(agency.subsTillDT) : null;
				if (!expiresDT) return false;

				const expireDT = startOfDay(expiresDT);
				const fromDT = filters.expiresRange.fromDT ? startOfDay(filters.expiresRange.fromDT) : null;
				const toDT = filters.expiresRange.toDT ? startOfDay(filters.expiresRange.toDT) : null;

				if (fromDT && toDT && !isWithinInterval(expireDT, { start: fromDT, end: toDT })) return false;
				if (fromDT && isBefore(expireDT, fromDT)) return false;
				if (toDT && isAfter(expireDT, toDT)) return false;
			}

			if (filters.noActions) {
				const hasNoActions =
					agency.userCnt === 1 &&
					agency.connCnt === 0 &&
					agency.thrCnt === 0 &&
					agency.tskCnt === 0 &&
					agency.ctrcCnt === 0 &&
					agency.txnCnt === 0;
				if (!hasNoActions) return false;
			}

			else if (filters.anyActions) {
				const hasAnyActions =
					agency.userCnt >= 1 &&
					(agency.connCnt > 0 ||
						agency.thrCnt > 0 ||
						agency.tskCnt > 0 ||
						agency.ctrcCnt > 0 ||
						agency.txnCnt > 0 ||
						agency.userCnt > 1);
				if (!hasAnyActions) return false;
			}

			if (filters.activeToday) {
				const today = startOfDay(new Date());
				if (!agency.lastActiveDT) return false;
				const lastActive = new Date(agency.lastActiveDT);
				if (!isSameDay(startOfDay(lastActive), today)) return false;
			}

			if (filters.onlineToday) {
				const today = startOfDay(new Date());
				if (!agency.lastSyncDT) return false;
				const lastSync = new Date(agency.lastSyncDT);
				if (!isSameDay(startOfDay(lastSync), today)) return false;
			}

			if (filters.repeatUsers) {
				const today = startOfDay(new Date());
				const now = new Date();

				if (!agency.lastActiveDT) return false;
				const lastActive = new Date(agency.lastActiveDT);
				if (!isSameDay(startOfDay(lastActive), today)) return false;

				if (!agency.sinceDT) return false;
				const joinedDate = new Date(agency.sinceDT);
				if (!isBefore(startOfDay(joinedDate), today)) return false;

				if (agency.subsTillDT) {
					const subsTillDate = new Date(agency.subsTillDT);
					if (subsTillDate < now) return false;
				}
			}

			if (filters.upgdOnRange?.fromDT || filters.upgdOnRange?.toDT) {
				const upgdDT = agency.lastSubDT ? startOfDay(new Date(agency.lastSubDT)) : null;
				if (!upgdDT) return false;

				const fromDT = filters.upgdOnRange.fromDT ? startOfDay(filters.upgdOnRange.fromDT) : null;
				const toDT = filters.upgdOnRange.toDT ? startOfDay(filters.upgdOnRange.toDT) : null;

				if (fromDT && toDT && !isWithinInterval(upgdDT, { start: fromDT, end: toDT })) return false;
				if (fromDT && isBefore(upgdDT, fromDT)) return false;
				if (toDT && isAfter(upgdDT, toDT)) return false;
			}

			return true;
		});
	}, [searchTerm, agencies, filters]);

	useEffect(() => {
		setCurrentPage(1);
	}, [searchTerm, filters]);

	const currentAgencies = useMemo(() => {
		const sorted = [...filteredAgencies].sort((a, b) => {
			if (!sortConfig.key) {
				const aDT = a.lastActiveDT ? new Date(a.lastActiveDT).getTime() : 0;
				const bDT = b.lastActiveDT ? new Date(b.lastActiveDT).getTime() : 0;
				return bDT - aDT;
			}

			let aValue = a[sortConfig.key] ?? '';
			let bValue = b[sortConfig.key] ?? '';

			if (sortConfig.key === 'edition') {
				aValue = a.ednName || '-';
				bValue = b.ednName || '-';
				return sortConfig.direction === 'asc'
					? aValue.toString().localeCompare(bValue.toString())
					: bValue.toString().localeCompare(aValue.toString());
			}

			if (sortConfig.key === 'inactivityDays') {
				aValue = aValue === '-' ? Infinity : Number(aValue) || 0;
				bValue = bValue === '-' ? Infinity : Number(bValue) || 0;
				return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
			}

			if (numericKeys.includes(sortConfig.key)) {
				aValue = Number(aValue) || 0;
				bValue = Number(bValue) || 0;
				return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
			}

			if (dateKeys.includes(sortConfig.key)) {
				const aDT = aValue ? new Date(aValue).getTime() : 0;
				const bDT = bValue ? new Date(bValue).getTime() : 0;
				return sortConfig.direction === 'asc' ? aDT - bDT : bDT - aDT;
			}

			return sortConfig.direction === 'asc'
				? aValue.toString().localeCompare(bValue.toString())
				: bValue.toString().localeCompare(aValue.toString());
		});

		return sorted.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
	}, [filteredAgencies, sortConfig, currentPage, rowsPerPage]);

	const totalPages = Math.ceil(filteredAgencies.length / rowsPerPage);

	const handleWspMessageClick = useCallback((agency, e) => {
		if (e) e.stopPropagation();
		setSelectedWspAgency(agency);
		setIsWspModalOpen(true);
	}, []);

	const renderContactIcons = useCallback((agency) => (
		<div className={`${usageMISClasses.flexRow} ${usageMISClasses.gap5} text-lg justify-end items-center`}>
			{(agency.agencyID || agency.mobile) && (
				<button
					type="button"
					title="Send WhatsApp Template"
					onClick={(e) => handleWspMessageClick(agency, e)}
					className="focus:outline-none flex items-center justify-center cursor-pointer"
				>
					<MessageSquare className={`${usageMISClasses.iconMd} text-sahi-green hover:opacity-80`} />
				</button>
			)}
			{(agency.agencyID) && (
				<a
					href={`${window.location.origin}/web/store?m=${agency.agencyID}`}
					target="_blank"
					rel="noopener noreferrer"
					onClick={e => e.stopPropagation()}
				>
					<Globe className={`${usageMISClasses.iconMd} text-black`} />
				</a>
			)}
			{agency.mobile && (
				<a href={`tel:+${agency.cc}${agency.mobile}`} onClick={e => e.stopPropagation()}>
					<MdPhone className={`${usageMISClasses.iconMd} text-black`} />
				</a>
			)}
			{agency.mobile && (
				<a
					href={`https://wa.me/${agency.cc}${agency.mobile}`}
					target="_blank"
					rel="noopener noreferrer"
					className={todayColor}
					onClick={e => e.stopPropagation()}
				>
					<BsWhatsapp className={`${usageMISClasses.iconMd} hover:bg-sahi-green-light`} />
				</a>
			)}
		</div>
	), [todayColor, handleWspMessageClick]);

	const MobileCard = memo(({ agency }) => (
		<div
			onClick={() => handleRowClick(agency)}
			className={usageMISClasses.mobileCard}
		>
			<div className={usageMISClasses.mobileHeader}>
				<div className={`${usageMISClasses.flexCol} justify-between gap-1 items-start`}>
					<span className={`${usageMISClasses.textBold} ${usageMISClasses.textMain} break-words ${usageMISClasses.textSm}`}>
						{agency.agencyName || '-'}
					</span>
					<span className={`${usageMISClasses.textXs} ${usageMISClasses.textSub}`}>
						{agency.ednName || '-'}
					</span>
				</div>
				<div className={`${usageMISClasses.flexCol} justify-between gap-1 text-right`}>
					<span className={`${usageMISClasses.textXs} ${usageMISClasses.textSub}`}>
						Since: {agency.sinceDTF || '-'}
					</span>
					<span className={`${usageMISClasses.textXs} ${usageMISClasses.textSub}`}>
						Active: <span>{agency.lastActiveDTF || '-'}</span>
					</span>
					{agency.lastSyncDT && (
						<span className={`${usageMISClasses.textXs} ${usageMISClasses.textSub}`}>
							Online: <span>{agency.lastSyncDTF || '-'}</span>
						</span>
					)}
				</div>
			</div>
			<div className={usageMISClasses.mobileFooter}>
				<div className={`flex flex-wrap ${usageMISClasses.gap3} items-center`}>
					<span>Contacts: <FormatAmount amount={agency.connCnt} showSymbol={false} amtClassName="text-xs" /></span>
					<span>Contracts: <FormatAmount amount={agency.ctrcCnt} showSymbol={false} amtClassName="text-xs" /></span>
					<span>Users: <FormatAmount amount={agency.userCnt} showSymbol={false} amtClassName="text-xs" /></span>
				</div>
				{renderContactIcons(agency)}
			</div>
		</div>
	));

	const renderJoinedOnHoverCard = useCallback((agency) => {
		const hasConnectionDetails = agency.connectionDetails?.sinceDT || agency.connectionDetails?.source;
		const hasThreadDetails = agency.threadDetails?.sinceDT || agency.threadDetails?.source;

		if (!hasConnectionDetails && !hasThreadDetails) return null;

		return (
			<HoverCardContent
				className={usageMISClasses.hoverCard}
				side="bottom"
			>
				<div className={usageMISClasses.hoverCardHeader}>
					{agency.agencyName || '-'}
				</div>
				<div className="px-3 py-2 space-y-2">
					<div className="flex gap-1">
						<span className="font-semibold">{`Joined on${agency.signUpSrc ? ` (${agency.signUpSrc})` : ''}:`}</span>
						<span className="text-sm">{agency.sinceDT ? formatDateWithTime(agency.sinceDT) : '-'}</span>
					</div>
					{hasConnectionDetails && (
						<div className="flex gap-1">
							<span className="font-semibold">
								Contact {agency.connectionDetails.source ? `(${agency.connectionDetails.source})` : ''}:
							</span>
							<span className="text-sm">
								{formatDateWithTime(agency.connectionDetails.sinceDT, '-')}
							</span>
						</div>
					)}
					{hasThreadDetails && (
						<div className="flex gap-1">
							<span className="font-semibold">
								Inquiry {agency.threadDetails.source ? `(${agency.threadDetails.source})` : ''}:
							</span>
							<span className="text-sm">
								{formatDateWithTime(agency.threadDetails.sinceDT, '-')}
							</span>
						</div>
					)}
				</div>
			</HoverCardContent>
		);
	}, []);

	const renderHoverCardContent = ((agency, typWiseCnt, isTransaction = false) => {
		if (!typWiseCnt || typWiseCnt.length === 0) return null;

		const lastDT = isTransaction ? agency.lastTxnDT : agency.lastThrDT;
		const label = isTransaction ? 'Last Transaction' : 'Last Thread';

		return (
			<HoverCardContent
				className={usageMISClasses.hoverCard}
				side="bottom"
			>
				<div className={usageMISClasses.hoverCardHeader}>
					{agency.agencyName || '-'}
				</div>

				{lastDT && (
					<div className="px-2 py-1 border-b border-gray-200">
						<div className="flex gap-1">
							<span className="font-semibold">{label}:</span>
							<span>
								{formatDateWithTime(lastDT)}
							</span>
						</div>
					</div>
				)}

				<div className="py-1">
					{isTransaction ? (
						typWiseCnt.map(currGroup => (
							<div key={currGroup.Typ}>
								{currGroup.types.map(currType => (
									<div
										key={currType.txnTypeID}
										className={usageMISClasses.hoverCardItem}
									>
										<ThreadSahiIcon tID={currType.txnTypeID} Xns={true} className={usageMISClasses.iconSm} size={16} />
										<p>{`${currType.txnTypeName}: ${currType.count}`}</p>
									</div>
								))}
							</div>
						))
					) : (
						typWiseCnt.map(currType => (
							<div key={currType.thrTypID} className={usageMISClasses.hoverCardItem}>
								<ThreadSahiIcon tID={currType.thrTypID} className={usageMISClasses.iconSm} />
								<p>{`${currType.thrTypName}: ${currType.count}`}</p>
							</div>
						))
					)}
				</div>
			</HoverCardContent>
		);
	});

	const renderTaskHoverCardContent = (agency) => {
		const followUpCnt = agency.connTskCnt || 0;
		const generalTaskCnt = Math.max(0, (agency.tskCnt || 0) - followUpCnt);

		return (
			<HoverCardContent
				className={usageMISClasses.hoverCard}
				side="bottom"
			>
				<div className={usageMISClasses.hoverCardHeader}>
					{agency.agencyName || '-'}
				</div>

				{agency.lastTskDT && (
					<div className="px-2 pt-1">
						<div className="flex gap-1">
							<span className="font-semibold">Last Task:</span>
							<span>
								{formatDateWithTime(agency.lastTskDT)}
							</span>
						</div>
					</div>
				)}

				{agency.lastTaskActionDT && (
					<div className="px-2 py-1 border-b border-gray-200">
						<div className="flex gap-1">
							<span className="font-semibold">Last Task Action:</span>
							<span>
								{formatDateWithTime(agency.lastTaskActionDT)}
							</span>
						</div>
					</div>
				)}

				<div className="py-1">
					<div className={usageMISClasses.hoverCardItem}>
						<UserCheck className={`${usageMISClasses.iconSm} text-sahi-blue`} />
						<p>{`Follow-ups: ${followUpCnt}`}</p>
					</div>
					<div className={usageMISClasses.hoverCardItem}>
						<ListTodo className={`${usageMISClasses.iconSm} text-sahi-green`} />
						<p>{`Tasks: ${generalTaskCnt}`}</p>
					</div>
				</div>
			</HoverCardContent>
		);
	};
	const renderDateTooltip = useCallback((date, formattedDate) => {
		if (!date) return '-';

		return (
			<Tooltip>
				<TooltipTrigger className={`${formattedDate === 'Today' ? todayColor : ''}`} asChild>
					<span className="cursor-pointer">{formattedDate}</span>
				</TooltipTrigger>
				<TooltipContent side="bottom" sideOffset={5}>
					<p>{formatDateWithTime(date)}</p>
				</TooltipContent>
			</Tooltip>
		);
	}, []);

	const renderCntWithDTTooltip = ((count, lastDT) => {
		if (!lastDT) {
			return <span>{count || '-'}</span>;
		}

		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<span>
						{typeof count === 'number' ? <FormatAmount amount={count} showCurr={false} /> : (count || '-')}
					</span>
				</TooltipTrigger>
				<TooltipContent side="bottom" sideOffset={5}>
					<p>{formatDateWithTime(lastDT)}</p>
				</TooltipContent>
			</Tooltip>
		);
	});

	const TooltipWrapper = ({ children, text }) => (
		<Tooltip>
			<TooltipTrigger asChild>{children}</TooltipTrigger>
			<TooltipContent side="top" sideOffset={5}>
				<p>{text}</p>
			</TooltipContent>
		</Tooltip>
	);

	const renderTableCell = useCallback((content, isToday = false, align = 'left') => {
		const baseClass = align === 'right' ? usageMISClasses.tableCellRight : usageMISClasses.tableCell;
		const className = `${baseClass} ${usageMISClasses.tableCellMin} ${usageMISClasses.tableCellEllipsis} ${isToday ? todayColor : ''}`;

		return (
			<TableCell className={className}>
				{content || '-'}
			</TableCell>
		);
	}, [todayColor]);

	const dateRangeFilters = () => (
		<div className="flex items-center gap-3 flex-wrap">
			<DatePickerWithRange
				label="Joined on"
				initialDateFrom={filters.joinedRange?.fromDT}
				initialDateTo={filters.joinedRange?.toDT}
				onUpdate={({ range }) => {
					setFilters(p => ({
						...p,
						joinedRange: { fromDT: range.fromDT, toDT: range.toDT },
					}));
				}}
				disableFuture={true}
			/>
			<DatePickerWithRange
				label="Expires on"
				initialDateFrom={filters.expiresRange?.fromDT}
				initialDateTo={filters.expiresRange?.toDT}
				onUpdate={({ range }) => {
					setFilters(p => ({
						...p,
						expiresRange: { fromDT: range.fromDT, toDT: range.toDT },
					}));
				}}
			/>
			<DatePickerWithRange
				label="Upgraded on"
				initialDateFrom={filters.upgdOnRange?.fromDT}
				initialDateTo={filters.upgdOnRange?.toDT}
				onUpdate={({ range }) => {
					setFilters(p => ({
						...p,
						upgdOnRange: { fromDT: range.fromDT, toDT: range.toDT },
					}));
				}}
				disableFuture={true}
			/>
			<div className="flex items-center gap-2">
				<span className="text-sm font-medium text-text-color whitespace-nowrap">
					Active in last
				</span>
				<Input
					type="tel"
					min="0"
					inputMode="numeric"
					pattern="[0-9]*"
					value={activeDays}
					onChange={(e) => {
						const value = e.target.value;
						if (/^\d*$/.test(value)) {
							setActiveDays(value);
							const num = value.trim() === '' ? null : parseInt(value, 10);
							setFilters(p => ({
								...p,
								activeDays: num !== null && !isNaN(num) && num >= 0 ? num : null
							}));
						}
					}}
					placeholder=""
					className="w-12 px-2 h-10 filter-border text-right"
					maxLength={3}
				/>
				<span className="text-sm font-medium text-text-color">days</span>
			</div>
			<div className="flex items-center gap-2">
				<label className="flex items-center cursor-pointer select-none">
					<input
						type="checkbox"
						checked={noActionsChecked}
						onChange={(e) => {
							const checked = e.target.checked;
							setNoActionsChecked(checked);
							if (checked) {
								setAnyActionsChecked(false);
								setFilters(p => ({ ...p, noActions: true, anyActions: false }));
								toast.success('Showing agencies with no actions.');
							} else {
								setFilters(p => ({ ...p, noActions: false }));
								toast.success('No Actions filter removed.');
							}
						}}
						className="mr-2 h-4 w-4 rounded border-gray-300 filter-border text-sahi-dark-blue focus:ring-sahi-dark-blue"
					/>
					<span className="text-sm font-medium text-text-color whitespace-nowrap">
						No Actions
					</span>
				</label>
			</div>
			<div className="flex items-center gap-2">
				<label className="flex items-center cursor-pointer select-none">
					<input
						type="checkbox"
						checked={anyActionsChecked}
						onChange={(e) => {
							const checked = e.target.checked;
							setAnyActionsChecked(checked);
							if (checked) {
								setNoActionsChecked(false);
								setFilters(p => ({ ...p, anyActions: true, noActions: false }));
								toast.success('Showing agencies with actions taken.');
							} else {
								setFilters(p => ({ ...p, anyActions: false }));
								toast.success('Any Actions filter removed.');
							}
						}}
						className="mr-2 h-4 w-4 rounded border-gray-300 filter-border text-sahi-dark-blue focus:ring-sahi-dark-blue"
					/>
					<span className="text-sm font-medium text-text-color whitespace-nowrap">
						Any Actions
					</span>
				</label>
			</div>
		</div>
	);

	const renderWebdataTable = () => {
		return (
			<Card className={`${usageMISClasses.cardHidden}`} style={{ width: 'calc(100vw - 133px)' }}>
				<div
					ref={tableContainerRef}
					className={`px--5 !${usageMISClasses.cardRadius} overflow-x-auto overflow-y-auto`}
					style={{ maxHeight: 'calc(100vh - 300px)' }}
					onScroll={(e) => {
						if (isScrollingRef.current) {
							e.currentTarget.scrollLeft = scrollPositionRef.current.scrollX;
							e.currentTarget.scrollTop = scrollPositionRef.current.scrollY;
							return;
						}

						const currentX = e.currentTarget.scrollLeft;
						const currentY = e.currentTarget.scrollTop;
						const lockedX = scrollPositionRef.current.scrollX;
						const lockedY = scrollPositionRef.current.scrollY;

						if (!isScrollingRef.current && (currentX !== lockedX || currentY !== lockedY)) {
							scrollPositionRef.current = {
								scrollX: currentX,
								scrollY: currentY,
							};
						} else if (!isScrollingRef.current) {
							scrollPositionRef.current = {
								scrollX: currentX,
								scrollY: currentY,
							};
						}
					}}
				>
					<CardContent className={`px--5 !${usageMISClasses.cardRadius}`}>
						<Table className="min-w-full table-auto" isOverflowAuto={false}>
							<TableHeader className="sticky top-0 z-10 bg-sahi-white">
								<TableRow className={`${usageMISClasses.card} py-1.5 h-11`}>
									{tableHeaderColumns
										.filter(col => {
											if (col.key === 'agencyName' || col.key === 'contactName') {
												return true;
											}
											if (col.key === 'edition') {
												return showEdition && columnVisibility[col.key] !== false;
											}
											return columnVisibility[col.key] !== false;
										})
										.map((currColumn, currIndex) => {
											return (
												<TableHead
													key={currColumn.key}
													className={`${usageMISClasses.textBold} ${usageMISClasses.textSm} text-black whitespace-nowrap ${currColumn.className}`}
													onClick={(e) => {
														e.stopPropagation();
														sortData(currColumn.key);
													}}
													ref={el => (headerRefs.current[currIndex] = el)}
												>
													<div className="flex items-center cursor-pointer">
														{currColumn.label}
														{sortConfig.key === currColumn.key &&
															(sortConfig.direction === 'asc' ? (
																<ArrowUp className={`ml-1 ${usageMISClasses.iconSm} text-black`} />
															) : (
																<ArrowDown className={`ml-1 ${usageMISClasses.iconSm} text-black`} />
															))}
													</div>
												</TableHead>
											);
										})}
									<TableHead className="text-left pl-4" ref={el => (headerRefs.current[tableHeaderColumns.length] = el)} />
								</TableRow>
							</TableHeader>
							<TableBody>
								{currentAgencies.map((currAgency, currIndex) => (
									<TableRow
										key={currAgency.agencyID || currIndex}
										onClick={(e) => handleRowClick(currAgency, e)}
										className={usageMISClasses.tableRow}
									>
										<TableCell className={`uniform-spacing pl-4 min-w-[220px] ${usageMISClasses.textMedium} ${usageMISClasses.textMain} ${usageMISClasses.tableCellEllipsis}`}>
											{currAgency.agencyName || '-'}
										</TableCell>
										<TableCell className={`uniform-spacing pl-4 min-w-[220px] ${usageMISClasses.textMedium} ${usageMISClasses.textMain} ${usageMISClasses.tableCellEllipsis}`}>
											{currAgency.contactName || '-'}
										</TableCell>
										<TableCell className={`text-left pl-4 min-w-[100px] max-w-[120px] ${usageMISClasses.tableCellEllipsis}`} style={{ display: columnVisibility.subType !== false ? 'table-cell' : 'none' }}>
											{currAgency.subType || '-'}
										</TableCell>
										{showEdition && columnVisibility.edition !== false && (
											<TableCell className={`text-left pl-4 min-w-[100px] max-w-[120px] ${usageMISClasses.tableCellEllipsis}`}>
												{currAgency.ednName || '-'}
											</TableCell>
										)}
										<TableCell className={`${usageMISClasses.tableCell} ${usageMISClasses.tableCellMin} ${usageMISClasses.tableCellEllipsis}`} style={{ display: columnVisibility.sinceDT !== false ? 'table-cell' : 'none' }}>
											{(currAgency.connectionDetails?.sinceDT || currAgency.connectionDetails?.source || currAgency.threadDetails?.sinceDT || currAgency.threadDetails?.source) ? (
												<HoverCard>
													<HoverCardTrigger className={`${currAgency.sinceDTF === 'Today' ? todayColor : ''}`} asChild>
														<span className="cursor-pointer" >{currAgency.sinceDTF || '-'}</span>
													</HoverCardTrigger>
													{renderJoinedOnHoverCard(currAgency)}
												</HoverCard>
											) : (
												renderDateTooltip(currAgency.sinceDT, currAgency.sinceDTF)
											)}
										</TableCell>

										<TableCell className={`${usageMISClasses.tableCell} ${usageMISClasses.tableCellMin} ${usageMISClasses.tableCellEllipsis}`} style={{ display: columnVisibility.lastSubDT !== false ? 'table-cell' : 'none' }}>
											{renderDateTooltip(currAgency.lastSubDT, currAgency.lastSubDTF)}
										</TableCell>

										<TableCell className={`${usageMISClasses.tableCell} ${usageMISClasses.tableCellMin} ${usageMISClasses.tableCellEllipsis}`} style={{ display: columnVisibility.subsTillDT !== false ? 'table-cell' : 'none' }}>
											{renderDateTooltip(currAgency.subsTillDT, currAgency.subsTillDTF)}
										</TableCell>

										<TableCell className={`${usageMISClasses.tableCell} ${usageMISClasses.tableCellMin} ${usageMISClasses.tableCellEllipsis}`} style={{ display: columnVisibility.lastActiveDT !== false ? 'table-cell' : 'none' }}>
											{renderDateTooltip(currAgency.lastActiveDT, currAgency.lastActiveDTF)}
										</TableCell>

										<TableCell className={`${usageMISClasses.tableCell} ${usageMISClasses.tableCellMin} ${usageMISClasses.tableCellEllipsis}`} style={{ display: columnVisibility.lastSyncDT !== false ? 'table-cell' : 'none' }}>
											{renderDateTooltip(currAgency.lastSyncDT, currAgency.lastSyncDTF)}
										</TableCell>

										{columnVisibility.userCnt !== false &&
											renderTableCell(<FormatAmount amount={currAgency.userCnt} showCurr={false} />, false, 'right')
										}
										<TableCell className={`${usageMISClasses.tableCellRight} ${usageMISClasses.tableCellMin} ${usageMISClasses.tableCellEllipsis}`} style={{ display: columnVisibility.connCnt !== false ? 'table-cell' : 'none' }}>
											{renderCntWithDTTooltip(currAgency.connCnt, currAgency.lastConnDT)}
										</TableCell>

										{columnVisibility.thrCnt !== false && (
											<HoverCard>
												<HoverCardTrigger asChild>
													<TableCell className={`${usageMISClasses.tableCellRight} ${usageMISClasses.tableCellMin} ${usageMISClasses.tableCellEllipsis}`}>
														{currAgency.thrTypWiseCnt?.length > 0 ? (
															<div className="cursor-pointer">
																<FormatAmount amount={currAgency.thrCnt} showCurr={false} />
																{renderHoverCardContent(currAgency, currAgency.thrTypWiseCnt, false)}
															</div>
														) : (
															<span>{currAgency.thrCnt || '-'}</span>
														)}
													</TableCell>
												</HoverCardTrigger>
											</HoverCard>
										)}

										{columnVisibility.tskCnt !== false && (
											currAgency.tskCnt > 0 ? (
												<HoverCard>
													<HoverCardTrigger asChild>
														<TableCell className={`${usageMISClasses.tableCellRight} ${usageMISClasses.tableCellMin} ${usageMISClasses.tableCellEllipsis}`}>
															<div className="cursor-pointer">
																<FormatAmount amount={currAgency.tskCnt} showCurr={false} />
																{renderTaskHoverCardContent(currAgency)}
															</div>
														</TableCell>
													</HoverCardTrigger>
												</HoverCard>
											) : (
												<TableCell className={`${usageMISClasses.tableCellRight} ${usageMISClasses.tableCellMin} ${usageMISClasses.tableCellEllipsis}`}>
													<span>-</span>
												</TableCell>
											)
										)}
										<TableCell className={`${usageMISClasses.tableCellRight} ${usageMISClasses.tableCellMin} ${usageMISClasses.tableCellEllipsis}`} style={{ display: columnVisibility.ctrcCnt !== false ? 'table-cell' : 'none' }}>
											{renderCntWithDTTooltip(currAgency.ctrcCnt, currAgency.lastCtrcDT)}
										</TableCell>

										{columnVisibility.txnCnt !== false && (
											<HoverCard>
												<HoverCardTrigger asChild>
													<TableCell className={`${usageMISClasses.tableCellRight} min-w-[100px] max-w-[120px] ${usageMISClasses.tableCellEllipsis}`}>
														{currAgency.txnTypWiseCnt?.length > 0 ? (
															<div className="cursor-pointer">
																<FormatAmount amount={currAgency.txnCnt} showCurr={false} />
																{renderHoverCardContent(currAgency, currAgency.txnTypWiseCnt, true)}
															</div>
														) : (
															<span>{currAgency.txnCnt || '-'}</span>
														)}
													</TableCell>
												</HoverCardTrigger>
											</HoverCard>
										)}

										{/* {renderTableCell(currAgency.lastConnDTF, currAgency.lastConnDTF === 'Today')}
									{renderTableCell(currAgency.lastThrDTF, currAgency.lastThrDTF === 'Today')}
									{renderTableCell(currAgency.lastTskDTF, currAgency.lastTskDTF === 'Today')}
									{renderTableCell(currAgency.lastCtrcDTF, currAgency.lastCtrcDTF === 'Today')}
									{renderTableCell(currAgency.lastTxnDTF, currAgency.lastTxnDTF === 'Today')} */}
										<TableCell className={`${usageMISClasses.tableCellRight} ${usageMISClasses.tableCellMin} ${usageMISClasses.tableCellEllipsis}`} style={{ display: columnVisibility.upgdAttemptCnt !== false ? 'table-cell' : 'none' }}>
											{renderCntWithDTTooltip(currAgency.upgdAttemptCnt, currAgency.lastUpgdAttemptDT)}
										</TableCell>
										{/* {renderTableCell(currAgency.lastUpgdAttemptDTF, currAgency.lastUpgdAttemptDTF === 'Today')}
									{renderTableCell(currAgency.lastThrSentDTF, currAgency.lastThrSentDTF === 'Today')}
									{renderTableCell(currAgency.lastSentiReqDTF, currAgency.lastSentiReqDTF === 'Today')} */}

										<TableCell className={`text-left pl-4 min-w-[80px] max-w-[100px] ${usageMISClasses.tableCellEllipsis} ${currAgency.inactivityDays === 0 ? todayColor : ''}`} style={{ display: columnVisibility.inactivityDays !== false ? 'table-cell' : 'none' }}>
											{currAgency.inactivityDays === '-' ? '-' : currAgency.inactivityDays === 0 ? 'Active' : `${currAgency.inactivityDays} ${currAgency.inactivityDays === 1 ? 'day' : 'days'}`}
										</TableCell>
										<TableCell className="pl-4 text-right min-w-[60px]">
											<div className="flex justify-end items-center px-2 xl:px-4 gap-2">
												<ContactActions connectionData={currAgency} type='MIS' />
												{(currAgency.agencyID || currAgency.mobile) && (
													<TooltipWrapper text="Send WhatsApp Template">
														<Button
															variant="ghost"
															size="sm"
															className="h-6 w-6 p-0 flex items-center justify-center rounded-md hover:shadow-sm transform-gpu transition-transform transition-colors duration-200 ease-out hover:scale-110 active:scale-95 hover:bg-sahi-green-light"
															onClick={(e) => handleWspMessageClick(currAgency, e)}
														>
															<MessageSquare className="h-4 w-4 text-sahi-green" />
														</Button>
													</TooltipWrapper>
												)}
												{(currAgency.agencyID || currAgency.mobile) && (
													<TooltipWrapper text="Webstore Progress">
														<Button
															variant="ghost"
															size="sm"
															className="h-6 w-6 p-0 flex items-center justify-center rounded-md hover:shadow-sm transform-gpu transition-transform transition-colors duration-200 ease-out hover:scale-110 active:scale-95 hover:bg-sahi-blue-light"
															onClick={(e) => handleWebstoreClick(currAgency, e)}
														>
															<Globe className="h-4 w-4 text-black" />
														</Button>
													</TooltipWrapper>
												)}
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</CardContent>
				</div>
			</Card>
		);
	};

	const renderSearchInput = useCallback(() => (
		<div className={usageMISClasses.searchContainer}>
			<Search className={usageMISClasses.searchIcon} />
			<Input
				placeholder="Search"
				value={searchTerm}
				onChange={(e) => setSearchTerm(e.target.value)}
				className={usageMISClasses.searchInput}
			/>
		</div>
	), [searchTerm]);

	const agencyStats = () => {

		const agenciesInRange = filteredAgencies.length;

		const today = startOfDay(new Date());
		const now = new Date();

		const activeToday = filteredAgencies.filter(a => {
			if (!a.lastActiveDT) return false;
			const lastActive = new Date(a.lastActiveDT);
			return isSameDay(startOfDay(lastActive), today);
		}).length;

		const onlineToday = filteredAgencies.filter(a => {
			if (!a.lastSyncDT) return false;
			const lastSync = new Date(a.lastSyncDT);
			return isSameDay(startOfDay(lastSync), today);
		}).length;

		const repeatUsers = filteredAgencies.filter(a => {
			if (!a.lastActiveDT) return false;
			const lastActive = new Date(a.lastActiveDT);
			if (!isSameDay(startOfDay(lastActive), today)) return false;

			if (!a.sinceDT) return false;
			const joinedDate = new Date(a.sinceDT);
			if (!isBefore(startOfDay(joinedDate), today)) return false;

			if (a.subsTillDT) {
				const subsTillDate = new Date(a.subsTillDT);
				if (subsTillDate < now) return false;
			}

			return true;
		}).length;

		const activityUsage = activeToday === 0 ? '0%' : `${((activeToday / agenciesInRange) * 100).toFixed(1)}%`;

		return (
			<div className="flex flex-wrap gap-3">
				<Button
					variant="outline"
					className={`w-auto bg-white text-black sahi-radius px-3 py-2 filter-border flex items-center h-full ${filters.activeToday ? 'filter-active cursor-pointer' : 'cursor-pointer hover:bg-gray-100'
						}`}
					onClick={() => {
						setFilters(p => ({ ...p, activeToday: !p.activeToday }));
						if (!filters.activeToday) {
							toast.success(`Showing agencies active today (${activeToday} agencies).`);
						} else {
							toast.success('Active Today filter cleared.');
						}
					}}
					title={filters.activeToday ? 'Click to clear filter' : 'Click to filter'}
				>
					<div className="flex items-center gap-2">
						<span className="text-sm font-normal text-black">
							Active Today:
						</span>
						<span className={`text-sm ${filters.activeToday ? 'text-sahi-dark-blue font-semibold' : 'text-text-color'}`}>
							{activeToday}
						</span>
						{filters.activeToday && (
							<svg className="w-4 h-4 text-sahi-dark-blue ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						)}
					</div>
				</Button>
				<Button
					variant="outline"
					className={`w-auto bg-white text-black sahi-radius px-3 py-2 filter-border flex items-center h-full ${filters.onlineToday ? 'filter-active cursor-pointer' : 'cursor-pointer hover:bg-gray-100'
						}`}
					onClick={() => {
						setFilters(p => ({ ...p, onlineToday: !p.onlineToday }));
						if (!filters.onlineToday) {
							toast.success(`Showing agencies online today (${onlineToday} agencies).`);
						} else {
							toast.success('Online Today filter cleared.');
						}
					}}
					title={filters.onlineToday ? 'Click to clear filter' : 'Click to filter'}
				>
					<div className="flex items-center gap-2">
						<span className="text-sm font-normal text-black">
							Online Today:
						</span>
						<span className={`text-sm ${filters.onlineToday ? 'text-sahi-dark-blue font-semibold' : 'text-text-color'}`}>
							{onlineToday}
						</span>
						{filters.onlineToday && (
							<svg className="w-4 h-4 text-sahi-dark-blue ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						)}
					</div>
				</Button>
				<Button
					variant="outline"
					className="w-auto bg-white text-black sahi-radius px-3 py-2 filter-border flex items-center cursor-default hover:bg-white h-full"
				>
					<div className="flex items-center gap-2">
						<span className="text-sm font-normal text-black">
							Total:
						</span>
						<span className="text-sm text-text-color">
							{agenciesInRange}
						</span>
					</div>
				</Button>
				<Button
					variant="outline"
					className={`w-auto bg-white text-black sahi-radius px-3 py-2 filter-border flex items-center h-full ${filters.repeatUsers ? 'filter-active cursor-pointer' : 'cursor-pointer hover:bg-gray-100'
						}`}
					onClick={() => {
						setFilters(p => ({ ...p, repeatUsers: !p.repeatUsers }));
						if (!filters.repeatUsers) {
							toast.success(`Showing repeat users (${repeatUsers} agencies).`);
						} else {
							toast.success('Repeat Users filter cleared.');
						}
					}}
					title={filters.repeatUsers ? 'Click to clear filter' : 'Click to filter'}
				>
					<div className="flex items-center gap-2">
						<span className="text-sm font-normal text-black">
							Repeat Users:
						</span>
						<span className={`text-sm ${filters.repeatUsers ? 'text-sahi-dark-blue font-semibold' : 'text-text-color'}`}>
							{repeatUsers}
						</span>
						{filters.repeatUsers && (
							<svg className="w-4 h-4 text-sahi-dark-blue ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						)}
					</div>
				</Button>
				<Button
					variant="outline"
					className="w-auto bg-white text-black sahi-radius px-3 py-2 filter-border flex items-center cursor-default hover:bg-white h-full"
				>
					<div className="flex items-center gap-2">
						<span className="text-sm font-normal text-black">
							Usage:
						</span>
						<span className="text-sm text-text-color">
							{activityUsage}
						</span>
					</div>
				</Button>
			</div>
		);
	};

	return (
		<TooltipProvider>
			<div className={`${usageMISClasses.spacing}  !overflow-x-visible`}>
				{!isMobile ? (
					<div className="hidden lg:block ">
						<div className={"flex justify-between items-center "}>
							<h1 className={`text-3xl ${usageMISClasses.textBold} ${usageMISClasses.textMain} mr-4 web-header-name`}>
								Usage MIS
							</h1>
							<div className='flex gap-3 '>
								<div className="flex-grow flex justify-end">
									{filteredAgencies.length > 0 && agencyStats()}
								</div>
								<div className={`flex items-center ${usageMISClasses.gap3} justify-end flex-grow`}>
									{agencies.length > 0 && renderSearchInput()}
								</div>
								<div className={`flex items-center justify-end flex-grow `}>
									{agencies.length > 0 && (
										<ColumnPreferencesDropdown
											type="MIS"
											columns={tableHeaderColumns.filter(col => col.key !== 'agencyName' && col.key !== 'contactName')}
											columnVisibility={columnVisibility}
											onUpdate={handleColumnVisibilityUpdate}
										/>
									)}
								</div>
								<div className={`flex items-center justify-end flex-grow `}>
									<Button
										className={cn(filterBtn, activeFilterClass, "relative", isFilterSelected && "filter-active")}
										onClick={() => setIsFilterSelected(prev => !prev)}
										aria-label="Toggle Filter"
									>
										<div className="h-6 w-6 flex items-center justify-center transition-all">
											<Filter
												className={cn(filterIconToggleClass)}
											/>
										</div>
										{hasActiveFilters && (
											<span className="absolute -top-1 -right-1 h-2.5 w-2.5 bg-sahi-dark-blue rounded-full border border-sahi-white" />
										)}
									</Button>
								</div>
							</div>
						</div>
					</div>
				) : (
					<div className={`${usageMISClasses.flexCol} lg:hidden ${usageMISClasses.gap3} pt-5 `}>
						{agencies.length > 0 && (
							<div className={`w-full flex items-center ${usageMISClasses.gap2}`}>
								<div className="relative flex-grow">
									<Search className={usageMISClasses.searchIcon} />
									<Input
										value={searchTerm}
										placeholder="Search agencies"
										onChange={(e) => setSearchTerm(e.target.value)}
										className="pl-10 h-10 w-full"
									/>
								</div>
							</div>
						)}

						<Button
							className={cn(filterBtn, activeFilterClass, "relative")}
							onClick={() => setIsFilterSelected(true)}
							aria-label="Toggle Filter"
						>
							<div className="h-6 w-6 flex items-center justify-center transition-all">
								<Filter
									className={cn(filterIconToggleClass)}
								/>
							</div>
							{hasActiveFilters && (
								<span className="absolute -top-1 -right-1 h-2.5 w-2.5 bg-sahi-dark-blue rounded-full border border-sahi-white" />
							)}
						</Button>
					</div>
				)}

				{!isMobile && (
					<div className="hidden lg:block">
						<div className={`flex justify-between items-center ${usageMISClasses.gap3}`}>
							{isFilterSelected && (
								<div className={`flex items-center ${usageMISClasses.gap3} justify-start flex-wrap`}>
									<TooltipWrapper text="All Subscriptions">
										<div>
											<CommonFilter
												label="All Subscriptions"
												options={subOptions}
												value={subType}
												onChange={handleSubsChange}
												displayValue={(val) => val}
												hideAllOption={true}
												searchEnabled={false}
												showClearButton={false}
											/>
										</div>
									</TooltipWrapper>
									{showEdition && (
										<TooltipWrapper text="All Editions">
											<div>
												<CommonFilter
													label="All Editions"
													options={ednOptions}
													value={ednType}
													onChange={handleEdnChange}
													displayValue={(val) => val}
													hideAllOption={true}
													searchEnabled={false}
													showClearButton={false}
												/>
											</div>
										</TooltipWrapper>
									)}
									{agencies.length > 0 && dateRangeFilters()}
								</div>
							)}

						</div>
					</div>
				)}

				{agencies.length === 0 && (
					<div className={`no-records ${usageMISClasses.border} ${usageMISClasses.shadow} ${usageMISClasses.cardRadius}`}>
						No agency statistics found.
					</div>
				)}

				{agencies.length > 0 && filteredAgencies.length === 0 && (
					<div className={`${usageMISClasses.shadow} ${usageMISClasses.cardRadius} transition-colors duration-200 ${usageMISClasses.card} p-2 w-fit`}>
						<div className={usageMISClasses.textSub}>
							No agencies found.
						</div>
					</div>
				)}

				{agencies.length > 0 && filteredAgencies.length > 0 && (
					<>
						{!isMobile ? (
							renderWebdataTable()
						) : (
							<div className="space-y-4">
								{currentAgencies.map((currAgency, currIndex) => (
									<MobileCard key={currAgency.agencyID || currIndex} agency={currAgency} />
								))}
							</div>
						)}
						<Pagination
							currentPage={currentPage}
							totalPages={totalPages}
							rowsPerPage={rowsPerPage}
							onRowsPerPageChange={(size) => setRowsPerPage(size)}
							onPageChange={(page) => setCurrentPage(page)}
						/>
					</>
				)}

				{selectedAgency && (
					<ConnectionDetailsWrapper
						isOpen={isDetailOpen}
						onClose={handleDetailClose}
						connID={selectedAgency.connID}
					/>
				)}

				{isWebstoreModalOpen && selectedWebstoreAgency && (
					<WebstoreSetupProgressModal
						agency={selectedWebstoreAgency}
						open={isWebstoreModalOpen}
						onClose={() => {
							setIsWebstoreModalOpen(false);
							setSelectedWebstoreAgency(null);
						}}
					/>
				)}

				{isWspModalOpen && selectedWspAgency && (
					<UsageMISWspModal
						isOpen={isWspModalOpen}
						onClose={() => {
							setIsWspModalOpen(false);
							setSelectedWspAgency(null);
						}}
						agency={selectedWspAgency}
					/>
				)}

				<AlertBox
					open={isInvalidMoveAlertOpen}
					onOpenChange={(open) => {
						if (!open) {
							setIsInvalidMoveAlertOpen(false);
						} else {
							setIsInvalidMoveAlertOpen(open);
						}
					}}
					title="Invalid Move"
					description="This agency is not a contact of SahiClub."
					actions={[
						{
							label: "OK",
							variant: "action",
							value: "ok",
							className: "w-full sm:w-auto",
							icon: <Check className={usageMISClasses.iconSm} />
						}
					]}
					onAction={() => {
						setIsInvalidMoveAlertOpen(false);
						setTimeout(() => {
							if (isScrollingRef.current) {
								isScrollingRef.current = false;
							}
						}, 2000);
					}}
				/>
			</div>
		</TooltipProvider>
	);
};

export default UsageMIS;